import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import { MENUS_ITEMS_ROLE_VALUE_HIGHLIGHTED } from "@raiden/library/constants/menus/items";
import useTranslate from "@raiden/library/hooks/useTranslate";
import { memo, useMemo } from "react";
import { Href } from "../../../../../components/Href";
import { Z_INDEX } from "../../../../../constants/zIndex";
import {
  getMenuItemAdapterType,
  getMenuItemAdapterValues,
} from "../../../../../helpers/menus/getMenuItemAdapterValues";
import { useDelayedSetState } from "../../../../../hooks/useDelayedCall";

export const MainMenuItem = memo(
  /**
   * @typedef {Object} Props
   * @property {import("@raiden/library/types/Configuration").ConfigurationMenuItem} menuItem
   * @property {boolean} isActive
   * @property {(index: number | null) => void} setActiveMenuId
   * @property {boolean} withWhiteBackground
   */
  /**
   * @param {Props} props
   */
  function MainMenuItem({
    menuItem,
    isActive,
    setActiveMenuId,
    withWhiteBackground,
  }) {
    const translate = useTranslate();

    const type = getMenuItemAdapterType({ menuItem });

    const { delayedCall, cancelDelayedCall } = useDelayedSetState();

    const textColor = useMemo(() => {
      if (withWhiteBackground) {
        return isActive ? "brandPrimary.700" : "brandPrimary.900";
      }

      return isActive ? "white" : "brandSecondary.200";
    }, [isActive, withWhiteBackground]);

    if (type === undefined) {
      return null;
    }

    const {
      title,
      href: _href,
      target,
    } = getMenuItemAdapterValues({
      menuItem,
      translate,
    });

    const href = type === "page_category" ? undefined : _href;

    return (
      <Href
        href={href}
        target={href ? target : undefined}
        display="flex"
        h="full"
        w="full">
        {menuItem.role === MENUS_ITEMS_ROLE_VALUE_HIGHLIGHTED ? (
          <Flex alignItems="center" justifyContent="flex-end" w="full">
            <Button
              colorScheme="brandPrimary"
              h="52px"
              fontSize="1.25rem"
              fontWeight={400}>
              {title}
            </Button>
          </Flex>
        ) : (
          <Flex
            role="group"
            onMouseEnter={() => {
              if (isActive || menuItem.children.length === 0) {
                setActiveMenuId(menuItem.id);
              } else {
                delayedCall(() => {
                  setActiveMenuId(menuItem.id);
                }, 300);
              }
            }}
            onMouseLeave={() => {
              cancelDelayedCall();
              setActiveMenuId(null);
            }}
            onFocus={() => {
              setActiveMenuId(menuItem.id);
            }}
            zIndex={Z_INDEX.HEADER_PANEL}
            borderBottom="2px solid"
            _hover={{
              color: "white",
              borderBottom: "2px solid transparent",
            }}
            borderColor={isActive ? textColor : "transparent"}
            alignItems="center"
            tabIndex={0}
            w="full">
            <Flex flexGrow={1} flexBasis={0}>
              <Box w="full">
                <HStack justify="center" spacing="0" w="full">
                  <Text
                    variant="text-button"
                    whiteSpace="nowrap"
                    position="relative"
                    color={textColor}
                    py=".5rem"
                    textAlign="center">
                    {title}
                  </Text>
                </HStack>
              </Box>
            </Flex>
          </Flex>
        )}
      </Href>
    );
  },
);
